import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';
import { contactInfo } from '../data/mock';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguageNavigate } from '../utils/languageRouting';
import LanguageLink from './LanguageLink';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const languageNavigate = useLanguageNavigate();
  
  // Force re-render when language changes
  const currentLanguage = i18n.language;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation links with translation keys only
  const navLinks = [
    { path: '/', key: 'nav.home' },
    { path: '/about', key: 'nav.about' },
    { path: '/services', key: 'nav.services' },
    { path: '/what-is-stone-carpet', key: 'nav.whatIsStone' },
    { path: '/gallery', key: 'nav.gallery' },
    { path: '/faq', key: 'nav.faq' },
    { path: '/contact', key: 'nav.contact', hash: 'quick-contact' }
  ];

  const handleNavigation = (path, hash = null) => {
    languageNavigate(path);
    
    if (hash) {
      // Navigate with hash anchor
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const offset = 100; // Account for fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300);
    } else {
      // No hash - scroll to top
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/98 backdrop-blur-md shadow-lg border-b border-stone-200/50' 
          : 'bg-gradient-to-b from-black/40 to-transparent backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <LanguageLink to="/" onClick={() => handleNavigation('/')} className="flex items-center group">
            <img 
              src="/images/costa-stone-logo-master.svg" 
              alt="Costa Stone - Premium Stone Carpet Costa del Sol" 
              width="290"
              height="38"
              decoding="async"
              fetchpriority="high"
              className="h-7 sm:h-9 md:h-10 w-auto transition-transform group-hover:scale-105"
            />
          </LanguageLink>

          {/* Hamburger Menu Button - Always Visible */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-2 transition-colors ${
              isScrolled ? 'text-stone-800' : 'text-white drop-shadow-lg'
            }`}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Universal Menu (All Screen Sizes) */}
        {isMobileMenuOpen && (
          <div className="py-4 bg-white/95 backdrop-blur-xl border-t border-stone-200/50 rounded-b-2xl shadow-2xl">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavigation(link.path, link.hash)}
                  className={`text-base font-medium transition-colors px-4 py-2 rounded-lg text-left ${
                    location.pathname === link.path
                      ? 'text-[#519dc7] bg-[#519dc7]/10'
                      : 'text-stone-700 hover:text-[#519dc7] hover:bg-stone-50'
                  }`}
                >
                  {t(link.key)}
                </button>
              ))}
              <div className="px-4 pt-4 space-y-3 border-t border-stone-200/50">
                {/* Language Switcher */}
                <div className="flex justify-center pb-2">
                  <LanguageSwitcher />
                </div>
                
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center justify-center space-x-2 text-stone-700 hover:text-[#519dc7] transition-colors py-2"
                >
                  <Phone size={18} />
                  <span className="text-sm font-medium">{contactInfo.phone}</span>
                </a>
                <Button 
                  onClick={() => {
                    languageNavigate('/contact');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                    setIsMobileMenuOpen(false);
                  }} 
                  className="w-full brand-gradient hover:opacity-90 text-white"
                >
                  {t('common.getQuote')}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
